import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { userReviews } from '@/mocks/comics';

export default function AboutPage() {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormState({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9p0pv2qs38sj6jc7h70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormState({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩国漫画网',
    description: '韩国漫画网是一个专注于提供高质量免费韩漫在线阅读的平台，致力于为漫画爱好者打造最好的阅读体验。',
    url: 'https://www.koreanmanhua.com/about',
    about: {
      '@type': 'Organization',
      name: '韩国漫画网',
      description: '韩国漫画网为您提供免费高质量韩漫大全，在线阅读便捷流畅，高清画质与丰富内容满足您的阅读需求。',
      url: 'https://www.koreanmanhua.com/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '韩国漫画网的漫画都是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的！韩国漫画网所有漫画资源均可免费在线阅读，无需付费订阅。' } },
      { '@type': 'Question', name: '韩国漫画网的更新频率是怎样的？', acceptedAnswer: { '@type': 'Answer', text: '我们每天都会更新漫画内容，热门作品基本与韩国同步更新。' } },
      { '@type': 'Question', name: '韩国漫画网支持哪些设备阅读？', acceptedAnswer: { '@type': 'Answer', text: '韩国漫画网支持电脑、平板和手机等多种设备访问，采用响应式设计适配不同屏幕。' } },
      { '@type': 'Question', name: '如何联系韩国漫画网？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过本站的联系表单给我们留言，或发送邮件至 contact@koreanmanhua.com。' } },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="韩国漫画网是一个专注于提供高质量免费韩漫在线阅读的平台。了解我们的品牌故事、用户口碑和联系方式，加入千万读者的韩漫世界。"
        keywords="关于韩国漫画网,韩漫平台介绍,免费韩漫网站,漫画阅读平台"
        canonicalUrl="/about"
        ogType="website"
        schema={{ ...schema, ...faqSchema }}
      />

      <main>
        <Navbar />

        <div className="pt-16">
          {/* Header */}
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="w-full px-4 md:px-6 py-6">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]} />
              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mt-2">关于我们</h1>
            </div>
          </div>

          {/* Brand Story */}
          <section className="py-10 md:py-14 bg-background-50" aria-label="品牌故事">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-6">
                <a href="#brand-story" className="cursor-pointer">品牌故事</a>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    韩国漫画网成立于2022年，由一群热爱韩国漫画的资深读者共同创建。我们发现许多优秀的韩漫作品在国内难以找到高质量的阅读渠道，于是决定打造一个专注韩漫的免费在线阅读平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    经过多年的发展，韩国漫画网已经成长为国内最大的韩漫在线阅读平台之一。我们拥有超过1000部漫画作品的资源库，涵盖恋爱、奇幻、动作、悬疑等十余个分类，每天为超过百万读者提供优质的阅读服务。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的使命是让每一位漫画爱好者都能轻松享受到高质量的韩漫阅读体验。我们始终坚持免费策略，不断优化阅读体验，努力成为连接读者与优秀韩漫作品的最佳桥梁。
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20cozy%20reading%20corner%20with%20bookshelf%20and%20comfortable%20armchair%2C%20warm%20natural%20sunlight%20through%20large%20window%2C%20Japanese%20and%20Korean%20manga%20books%20on%20shelves%2C%20minimalist%20interior%20design%2C%20peaceful%20atmosphere%2C%20editorial%20photography%20style&width=600&height=400&seq=about-brand-01&orientation=landscape"
                    alt="韩国漫画网 - 舒适的阅读空间"
                    title="韩国漫画网 - 品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-10 md:py-14 bg-background-100" aria-label="平台特色">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-8 text-center">为什么选择我们</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {[
                  { icon: 'ri-shield-check-line', title: '完全免费', desc: '所有漫画免费阅读，没有隐藏收费，坚持为读者提供免费服务' },
                  { icon: 'ri-image-line', title: '高清画质', desc: '所有漫画均采用高清图片，为您呈现最清晰的阅读体验' },
                  { icon: 'ri-flashlight-line', title: '极速加载', desc: '优化的服务器和CDN加速，让阅读更加流畅无等待' },
                  { icon: 'ri-refresh-line', title: '实时更新', desc: '热门漫画每日同步更新，第一时间获取最新章节内容' },
                ].map((feat, i) => (
                  <div key={i} className="text-center p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                      <i className={`${feat.icon} text-xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 font-heading">{feat.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="py-10 md:py-14 bg-background-50" aria-label="用户评价">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-2 text-center">
                <a href="#user-reviews" className="cursor-pointer">用户口碑</a>
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">听听读者们怎么说</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {userReviews.map((review) => (
                  <div key={review.id} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary-500">{review.userName[0]}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{review.userName}</p>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }, (_, i) => (
                            <div key={i} className="w-3 h-3 flex items-center justify-center">
                              <i className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                    <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 bg-background-100" aria-label="联系我们">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-2 text-center">
                <a href="#contact-us" className="cursor-pointer">联系我们</a>
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">有任何问题或建议？欢迎随时联系我们</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">contact@koreanmanhua.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-wechat-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                      <p className="text-sm text-foreground-600">韩国漫画网</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">工作时间</h3>
                      <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70" data-readdy-form>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4">发送消息</h3>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <div className="space-y-3">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formState.name}
                        onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200/70 focus:border-primary-400 outline-none transition-colors text-foreground-950"
                        placeholder="请输入您的姓名"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={(e) => setFormState((s) => ({ ...s, email: e.target.value }))}
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200/70 focus:border-primary-400 outline-none transition-colors text-foreground-950"
                        placeholder="请输入您的邮箱"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formState.message}
                        onChange={(e) => setFormState((s) => ({ ...s, message: e.target.value }))}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200/70 focus:border-primary-400 outline-none transition-colors text-foreground-950 resize-none"
                        placeholder="请输入您的留言（最多500字）"
                        required
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">{formState.message.length}/500</p>
                    </div>
                  </div>

                  {(formStatus === 'success' || formStatus === 'error') && (
                    <div className={`mt-4 px-4 py-3 rounded-lg text-sm font-medium ${
                      formStatus === 'success' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                    }`}>
                      {formStatus === 'success' ? '感谢您的留言，我们会尽快回复！' : formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="mt-4 w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </main>
    </>
  );
}